/**
 * Auth deep links may use `?authMode=` or `#authMode=`. When both are present, the query value wins.
 *
 * react-router `location` includes `hash` at runtime, but some TS `Location` typings omit it;
 * use `getLocationHash` / `hasAuthModeDeepLinkFromLocation` when reading from router context.
 */
export function getLocationHash(location: unknown): string | undefined {
  if (typeof location !== 'object' || location === null) return undefined;
  const { hash } = location as { hash?: unknown };
  return typeof hash === 'string' && hash !== '' ? hash : undefined;
}

export function parseAuthModeFromHash(hash: string | undefined): string | undefined {
  if (!hash || hash.length <= 1) return undefined;
  const withoutLeadingHash = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!withoutLeadingHash) return undefined;
  try {
    const value = new URLSearchParams(withoutLeadingHash).get('authMode');
    return value === null || value === '' ? undefined : value;
  } catch {
    return undefined;
  }
}

export function getAuthMode(query: Record<string, unknown> | undefined, hash: string | undefined): string | undefined {
  const fromQuery = query?.authMode;
  if (fromQuery !== undefined && fromQuery !== null && String(fromQuery) !== '') {
    return String(fromQuery);
  }
  return parseAuthModeFromHash(hash);
}

/**
 * Removes one key from a URL hash fragment encoded like a query string (e.g. `#a=1&b=2`).
 * Preserves other keys. Returns `''` if nothing remains after `#`, otherwise `#` + remainder.
 * If `paramName` is empty, returns `hash ?? ''` unchanged.
 */
export function stripHashParam(hash: string | undefined, paramName: string): string {
  if (!paramName) {
    return hash ?? '';
  }
  if (!hash || hash.length <= 1) return '';
  const withoutLeadingHash = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(withoutLeadingHash);
  if (!params.has(paramName)) return hash;
  params.delete(paramName);
  const remainder = params.toString();
  return remainder ? `#${remainder}` : '';
}

/**
 * jsuri stores the fragment in `anchor` (often without a leading `#`). Mutates `uri.anchor` using {@link stripHashParam}.
 */
export function stripHashParamFromUriAnchor(uri: { anchor: (val?: string) => string }, paramName: string): void {
  const anchorSegment = uri.anchor();
  const normalizedHash =
    anchorSegment == null || anchorSegment === ''
      ? undefined
      : anchorSegment.startsWith('#')
      ? anchorSegment
      : `#${anchorSegment}`;
  const strippedHash = stripHashParam(normalizedHash, paramName);
  const newAnchor = strippedHash.startsWith('#') ? strippedHash.slice(1) : strippedHash;
  uri.anchor(newAnchor);
}

/**
 * Removes only `authMode` from the hash fragment, preserving other hash key/value pairs and the `#` prefix when needed.
 */
export function stripAuthModeFromHash(hash: string | undefined): string {
  return stripHashParam(hash, 'authMode');
}

/**
 * jsuri keeps URL fragments in `anchor` (usually without a leading `#`). After `deleteQueryParam('authMode')`,
 * call this so `#authMode=…` is removed using the same rules as {@link stripAuthModeFromHash}.
 */
export function stripAuthModeFromUriAnchor(uri: { anchor: (val?: string) => string }): void {
  stripHashParamFromUriAnchor(uri, 'authMode');
}

export function hasAuthModeDeepLink(query: Record<string, unknown> | undefined, hash: string | undefined): boolean {
  return getAuthMode(query, hash) !== undefined;
}

export function hasAuthModeDeepLinkFromLocation(location: unknown): boolean {
  const query =
    typeof location === 'object' && location !== null && 'query' in location
      ? (location as { query?: Record<string, unknown> }).query
      : undefined;
  return hasAuthModeDeepLink(query, getLocationHash(location));
}
